import { JANE_DEFINED_ROWS, buildFilter } from '@iheartjane/menu';
import type { Nullish } from '@iheartjane/types';
import kebabCase from 'lodash-es/kebabCase';

import { PRODUCT_ROOT_TYPES } from './constants';
import { formatFilterLabel } from './formatFilterLabel';

/** Takes a featuredCategory path param and returns the corresponding custom row title and filter */

type StoreSubset = {
  custom_product_type_labels?: Nullish<Record<string, string>>;
  id: string | number;
};

export const formatFeaturedCategory = (
  store: StoreSubset,
  featuredCategory?: string
): {
  featuredFilter: string;
  featuredTitle: string;
  isJaneDefined: boolean;
} => {
  if (!store) {
    return { featuredTitle: '', featuredFilter: '', isJaneDefined: false };
  }
  const { id, custom_product_type_labels: customLabels } = store;
  const janeCategories = [...JANE_DEFINED_ROWS, 'buy_again'];

  if (!featuredCategory)
    return { featuredTitle: '', featuredFilter: '', isJaneDefined: false };

  // encode/decode uri component for handling emojis
  // and use kebabCase to strip special characters from custom labels
  const pathToLabel = featuredCategory && decodeURIComponent(featuredCategory);

  // need to skip anything with a : or PRODUCT_ROOT_TYPES to omit category and subcategory labels that are not tied to custom rows
  const customFilterAndLabel = customLabels
    ? Object.entries(customLabels).filter(([filter, label]) => {
        const isCustomCategoryLabel = PRODUCT_ROOT_TYPES.includes(filter);
        const isMaybeSubcategoryLabel = filter.includes(':');
        const isCustomSubcategoryLabel =
          isMaybeSubcategoryLabel &&
          PRODUCT_ROOT_TYPES.includes(filter.split(':')[0]);

        return (
          !isCustomCategoryLabel &&
          !isCustomSubcategoryLabel &&
          (kebabCase(filter) === pathToLabel ||
            kebabCase(label) === pathToLabel)
        );
      })
    : [];

  // if a custom label exists
  if (customFilterAndLabel.length) {
    const [filter, label] = customFilterAndLabel[0];
    const isJaneDefined = janeCategories.includes(filter);

    const filterValue = isJaneDefined ? filter : label;
    const formattedFilter = isJaneDefined ? filter : `${filterValue}-${id}`;

    return {
      featuredTitle: label,
      featuredFilter: buildFilter('root_types', [formattedFilter]),
      isJaneDefined,
    };
  }

  const snakeCaseLabel = pathToLabel.split('-').join('_');

  // if no custom label but path is jane defined
  if (!customFilterAndLabel.length && janeCategories.includes(snakeCaseLabel)) {
    return {
      featuredTitle: formatFilterLabel(snakeCaseLabel, customLabels, id),
      featuredFilter:
        pathToLabel !== 'buy-again'
          ? buildFilter('root_types', [snakeCaseLabel])
          : '',
      isJaneDefined: true,
    };
  }

  return {
    featuredTitle: '',
    featuredFilter: '',
    isJaneDefined: false,
  };
};
